import {
  type ButtonHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
} from "react";
import { useInterruptCard } from "../InterruptCardContext";

/**
 * Respond control — collapsed it's a button labeled "Respond"; clicking
 * expands an inline textarea + submit. ⌘+Enter / Ctrl+Enter sends.
 *
 * Submits `{ type: "response", args: <text> }`. Renders null when
 * `interrupt.config.allow_respond` is false.
 *
 * Two visual states managed via `selectedType === "response"` in context:
 *   - false → trigger button
 *   - true  → textarea + Send / Cancel
 */
export interface RespondProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Label for the trigger button. Defaults to "Respond". */
  children?: ReactNode;
  /** Placeholder for the textarea. */
  placeholder?: string;
}

export function Respond({
  children,
  placeholder = "Tell the agent what to change…",
  onClick,
  ...props
}: RespondProps) {
  const {
    interrupt,
    selectedType,
    setSelectedType,
    responseText,
    setResponseText,
    submit,
    submitting,
  } = useInterruptCard();

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const expanded = selectedType === "response";

  useEffect(() => {
    if (expanded) textareaRef.current?.focus();
  }, [expanded]);

  if (!interrupt.config.allow_respond) return null;

  if (!expanded) {
    return (
      <button
        type="button"
        {...props}
        disabled={props.disabled || submitting}
        onClick={(e) => {
          onClick?.(e);
          if (e.defaultPrevented) return;
          setSelectedType("response");
        }}
        className={[
          "px-3 py-1.5 text-sm font-medium rounded-md",
          "bg-white text-zinc-900 border border-zinc-300 hover:bg-zinc-50",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          props.className,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {children ?? "Respond"}
      </button>
    );
  }

  const canSend = responseText.trim().length > 0 && !submitting;
  const send = () => {
    if (!canSend) return;
    void submit({ type: "response", args: responseText });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      <textarea
        ref={textareaRef}
        value={responseText}
        onChange={(e) => setResponseText(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={submitting}
        className="w-full min-h-[80px] rounded-md border border-zinc-300 p-2 text-sm font-sans
                   focus:outline-none focus:ring-2 focus:ring-zinc-400 disabled:opacity-50"
      />
      <div className="flex gap-2">
        <button
          type="button"
          disabled={!canSend}
          onClick={send}
          className="px-3 py-1.5 text-sm font-medium rounded-md
                     bg-zinc-900 text-white hover:bg-zinc-800
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Send
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={() => {
            setSelectedType(null);
            setResponseText("");
          }}
          className="px-3 py-1.5 text-sm font-medium rounded-md
                     text-zinc-600 hover:text-zinc-900"
        >
          Cancel
        </button>
        <span className="ml-auto text-xs text-zinc-400 self-center">
          ⌘ + Enter to send
        </span>
      </div>
    </div>
  );
}
